<template>
  <div v-show="visible" class="tab-pane-content">
    <!-- Table -->
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleGridEditClick"
      @button-click="handleGridButtonClick"
    />

    <!-- ClienteModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { whatsappNumber } from '../../composables/usePhoneFormat';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type ClienteId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown> & { _isFrozenFooter?: boolean };
  value?: unknown;
}

interface TabClientesProps {
  visible?: boolean;
  /** true = only clients registered inside the filter period ("Clientes no período") */
  periodo?: boolean;
}

const props = withDefaults(defineProps<TabClientesProps>(), {
  visible: false,
  periodo: false
});

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();

const dataTableRef = ref<DataTableInstance | null>(null);

// "Todos os clientes" ignores the period on purpose; "Clientes no período" is
// the same list cut by the filter's date window (backend does the cut).
const endpoint = computed(() =>
  props.periodo
    ? '/index.php/gestao_clientes/carrega_clientes_no_periodo'
    : '/index.php/gestao_clientes/carrega_clientes'
);

const tableConfig = computed(() => ({
  tableId: props.periodo ? 'tabelaClientesNoPeriodo' : 'tabelaClientes',
  title: props.periodo ? t('Clientes no período') : t('Clientes'),
  filename: `clientes_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pagination: true,
  pageLength: 25,
  heightMode: 'fill-viewport',
  rowSelection: 'single',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.cliente_id;
    return id == null ? '' : `cliente-row-${id}`;
  },
}));

const filterConfig = computed(() => ({
  columns: {
    'cliente_id': 'text',
    'data_cadastro': 'daterange',
    'franquia': 'select',
    'nome': 'text',
    'documento': 'text',
    'ddd': 'text',
    'telefone': 'text',
    'bairro': 'text',
    'localidade': 'text',
    'origem_nome': 'select',
    'status_label': 'select'
  }
}));

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: 'CSV', loading: false }));

const statusLabelRenderer = (params: CellParams): Node => {
  const row = (params.data || {}) as { status?: number };
  let html = '';
  if (row.status === 0) {
    html = `<span class="status-badge status-inactive">${t('Inativo', 'Inativo')}</span>`;
  } else if (row.status === 2) {
    html = `<span class="status-badge status-default">${t('Arquivado', 'Arquivado')}</span>`;
  } else {
    html = `<span class="status-badge status-active">${t('Ativo', 'Ativo')}</span>`;
  }
  const container = document.createElement('span');
  container.innerHTML = html;
  return container.firstChild as Node;
};

const tableColumns = computed(() => [
  {
    data: 'cliente_id',
    title: 'ID',
    width: 80,
    resizable: true,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (value: ClienteId | undefined, row: { cliente_id?: ClienteId } | undefined) => {
      if (value) {
        eventBus.emit('cliente.edit', value);
      } else if (row?.cliente_id) {
        eventBus.emit('cliente.edit', row.cliente_id);
      }
    },
    dataType: 'number',
  },
  {
    data: 'data_cadastro',
    title: t('Data cadastro', 'Data cadastro'),
    width: 160,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'datetime',
  },
  { data: 'franquia', title: t('Franquia', 'Franquia'), width: 150, resizable: true, sortable: true },
  { data: 'nome', title: t('Nome', 'Nome'), width: 220, resizable: true, sortable: true },
  { data: 'documento', title: t('Documento', 'Documento'), width: 120, resizable: true, sortable: true },
  { data: 'ddd', title: t('DDD', 'DDD'), width: 80, resizable: true, sortable: true },
  {
    data: 'telefone',
    title: 'WhatsApp',
    width: 180,
    resizable: true,
    cellRenderer: (params: CellParams) => {
      const row = params.data as { status?: number; telefone?: string; ddd?: string; ddi?: string };

      // O número completo vem de uma regra só (usePhoneFormat): país sem DDD também ganha botão.
      const numero = whatsappNumber(row);
      if (row.status == 0 || numero === null) {
        return '';
      }

      const link = document.createElement('a');
      link.className = 'p-button p-button-success p-button-sm btn-whatsapp';
      link.href = `https://api.whatsapp.com/send?phone=${numero}`;
      link.target = '_blank';
      link.title = 'WhatsApp';
      link.innerHTML = `<i class="pi pi-whatsapp" aria-hidden="true"></i> +${numero}`;

      return link;
    }
  },
  { data: 'bairro', title: t('Bairro', 'Bairro'), width: 140, resizable: true, sortable: true },
  { data: 'localidade', title: t('Localidade', 'Localidade'), width: 160, resizable: true, sortable: true },
  { data: 'origem_nome', title: t('Origem', 'Origem'), width: 140, resizable: true, sortable: true },
  { data: 'status_label', title: t('Status', 'Status'), width: 140, resizable: true, sortable: true, cellRenderer: statusLabelRenderer },
  {
    title: t('Orçamento', 'Orçamento'),
    width: 160,
    renderType: 'clickable_button',
    buttonText: t('Criar orçamento', 'Criar orçamento'),
    buttonIcon: 'pi pi-plus',
    buttonClass: 'p-button p-button-warning p-button-sm',
    buttonAction: 'novo-orcamento',
    buttonCondition: (row: { cliente_id?: ClienteId }) => !!row.cliente_id,
  },
]);

const loadData = (): void => {
  log('GestaoClientesTabClientes', 'loadData', 'Triggering grid refresh');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('GestaoClientesTabClientes', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  emit('update:total', data.length || 0);
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('GestaoClientesTabClientes', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : err.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar clientes: ') + message);
};

const handleGridEditClick = (clienteId: ClienteId): void => {
  log('GestaoClientesTabClientes', 'handleGridEditClick', 'Edit clicked from grid', clienteId);
  handleEditarCliente(clienteId);
};

const handleGridButtonClick = ({ value, data }: { value?: ClienteId; data?: { cliente_id?: ClienteId } }): void => {
  log('GestaoClientesTabClientes', 'handleGridButtonClick', 'Button clicked', { value });
  handleNovoOrcamento(value || data?.cliente_id);
};

const handleEditarCliente = (clienteId?: ClienteId): void => {
  log('GestaoClientesTabClientes', 'handleEditarCliente', 'Opening edit cliente modal', clienteId);
  if (clienteId) eventBus.emit('cliente.edit', clienteId);
};

const handleNovoOrcamento = (clienteId?: ClienteId): void => {
  log('GestaoClientesTabClientes', 'handleNovoOrcamento', 'Opening new orcamento modal', clienteId);
  eventBus.emit('orcamento.newForClientRequested', clienteId);
};

const handleClienteSaved = (): void => {
  if (props.visible) loadData();
};

const handleOrcamentoSaved = (): void => {
  if (props.visible) loadData();
};

onMounted(() => {
  log('GestaoClientesTabClientes', 'onMounted', 'Component mounted');

  eventBus.on('cliente.saved', handleClienteSaved);
  eventBus.on('orcamento.saved', handleOrcamentoSaved);
});

onBeforeUnmount(() => {
  log('GestaoClientesTabClientes', 'onBeforeUnmount', 'Cleaning up listeners');
  eventBus.off('cliente.saved', handleClienteSaved);
  eventBus.off('orcamento.saved', handleOrcamentoSaved);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.tab-pane-content {
  padding: 20px;
}
</style>

